import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, CheckCircle, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { useAuth } from '../context/AuthContext';

/**
 * DuplicateProductsModal - Détection et suppression des doublons
 * 
 * Détecte les produits avec le même code article + fournisseur
 * Permet de sélectionner et supprimer les doublons en masse
 */
export const DuplicateProductsModal = ({ open, onOpenChange, onDeleted }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDuplicates();
    } else {
      // Reset state on close
      setDuplicates([]);
      setSelectedIds(new Set());
    }
  }, [open]);

  const fetchDuplicates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/products/duplicates');
      setDuplicates(response.data.duplicates || []);
      setTotalGroups(response.data.total_groups || 0);
      setTotalDuplicates(response.data.total_duplicates || 0);
      
      if (response.data.total_groups === 0) {
        toast.success('✅ Aucun doublon détecté !');
      }
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      toast.error('Erreur lors de la détection des doublons');
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedIds(newSelected);
  };

  const selectAllInGroup = (products) => {
    const newSelected = new Set(selectedIds);
    // Garder le premier (le plus ancien), sélectionner tous les autres
    products.slice(1).forEach(p => newSelected.add(p.id));
    setSelectedIds(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Veuillez sélectionner au moins un produit à supprimer');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ Êtes-vous sûr de vouloir supprimer ${selectedIds.size} produit(s) ?\n\nCette action est irréversible.`
    );

    if (!confirmed) return;

    try {
      setDeleting(true);
      const response = await api.delete('/products/bulk', {
        data: { product_ids: Array.from(selectedIds) }
      });

      toast.success(`✅ ${response.data.deleted} produit(s) supprimé(s)`);
      
      // Refresh duplicates list
      await fetchDuplicates();
      
      // Notify parent to refresh products list
      if (onDeleted) {
        onDeleted();
      }

      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting products:', error);
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            Détection de Doublons
          </DialogTitle>
          <DialogDescription>
            {loading ? (
              'Recherche des doublons...'
            ) : totalGroups > 0 ? (
              <>
                <strong>{totalGroups}</strong> groupe(s) de doublons trouvé(s) • 
                <strong className="text-red-600"> {totalDuplicates}</strong> produit(s) en double
              </>
            ) : (
              'Aucun doublon détecté'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Analyse en cours...</p>
              </div>
            </div>
          ) : duplicates.length === 0 ? (
            <Alert className="m-4">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription>
                <strong>Aucun doublon trouvé !</strong> Tous vos produits ont des codes uniques par fournisseur.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4 p-4">
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  <strong>Instructions :</strong> Sélectionnez les produits à supprimer. 
                  Par défaut, nous recommandons de garder le <strong>premier produit</strong> (le plus ancien) 
                  de chaque groupe.
                </AlertDescription>
              </Alert>

              {duplicates.map((group, idx) => (
                <div key={idx} className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="destructive" className="text-base px-3 py-1">
                        {group.count} doublons
                      </Badge>
                      <div>
                        <p className="font-semibold text-lg">Code: {group.code}</p>
                        <p className="text-sm text-gray-600">{group.supplier_name}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => selectAllInGroup(group.products)}
                      data-testid={`select-group-duplicates-${idx}`}
                    >
                      Sélectionner les doublons
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {group.products.map((product, pidx) => (
                      <div
                        key={product.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          pidx === 0 
                            ? 'bg-green-50 border-green-200' 
                            : selectedIds.has(product.id)
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            checked={selectedIds.has(product.id)}
                            onCheckedChange={() => toggleProduct(product.id)}
                            disabled={pidx === 0}
                            data-testid={`duplicate-checkbox-${product.id}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{product.article}</p>
                              {pidx === 0 && (
                                <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                                  À conserver (le plus ancien)
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                              <span>Prix: {product.catalog_price ? `${product.catalog_price}€` : '-'}</span>
                              <span>•</span>
                              <span>Créé: {product.created_at ? new Date(product.created_at).toLocaleDateString('fr-FR') : '-'}</span>
                            </div>
                          </div>
                        </div>
                        {selectedIds.has(product.id) && (
                          <Trash2 className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t p-4 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedIds.size > 0 ? (
              <strong className="text-red-600">{selectedIds.size} produit(s) sélectionné(s)</strong>
            ) : (
              'Aucun produit sélectionné'
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fermer
            </Button>
            {duplicates.length > 0 && (
              <Button
                variant="destructive"
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0 || deleting}
                data-testid="delete-selected-duplicates-button"
              >
                {deleting ? (
                  <>Suppression...</>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer ({selectedIds.size})
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateProductsModal;
